import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

const INDIA_TOPO = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Major Indian cities with coordinates
const CITY_COORDS = {
  'mumbai': [72.8777, 19.0760],
  'delhi': [77.1025, 28.7041],
  'new delhi': [77.2090, 28.6139],
  'bangalore': [77.5946, 12.9716],
  'bengaluru': [77.5946, 12.9716],
  'chennai': [80.2707, 13.0827],
  'kolkata': [88.3639, 22.5726],
  'hyderabad': [78.4867, 17.3850],
  'pune': [73.8567, 18.5204],
  'ahmedabad': [72.5714, 23.0225],
  'jaipur': [75.7873, 26.9124],
  'lucknow': [80.9462, 26.8467],
  'kochi': [76.2673, 9.9312],
  'goa': [74.1240, 15.2993],
  'chandigarh': [76.7794, 30.7333],
  'bhopal': [77.4126, 23.2599],
  'indore': [75.8577, 22.7196],
  'nagpur': [79.0882, 21.1458],
  'coimbatore': [76.9558, 11.0168],
  'thiruvananthapuram': [76.9366, 8.5241],
  'vadodara': [73.1812, 22.3072],
  'surat': [72.8311, 21.1702],
  'visakhapatnam': [83.2185, 17.6868],
  'patna': [85.1376, 25.6093],
  'bhubaneswar': [85.8245, 20.2961],
  'dehradun': [78.0322, 30.3165],
  'guwahati': [91.7362, 26.1445],
  'ranchi': [85.3096, 23.3441],
  'mysore': [76.6394, 12.2958],
  'mysuru': [76.6394, 12.2958],
  'udaipur': [73.7125, 24.5854],
  'jodhpur': [73.0243, 26.2389],
  'varanasi': [82.9913, 25.3176],
  'pondicherry': [79.8083, 11.9416],
  'puducherry': [79.8083, 11.9416],
  'shimla': [77.1734, 31.1048],
  'gangtok': [88.6138, 27.3389],
  'shillong': [91.8933, 25.5788],
  'imphal': [93.9368, 24.8170],
  'agartala': [91.2868, 23.8315],
  'kohima': [94.1086, 25.6751],
  'aizawl': [92.7176, 23.7271],
  'itanagar': [93.6166, 27.0844],
  'srinagar': [74.7973, 34.0837],
  'jammu': [74.8570, 32.7266],
  'amritsar': [74.8723, 31.6340],
  'raipur': [81.6296, 21.2514],
  'aurangabad': [75.3433, 19.8762],
  'madurai': [78.1198, 9.9252],
  'tiruchirappalli': [78.6569, 10.7905],
  'mangalore': [74.8560, 12.9141],
  'mangaluru': [74.8560, 12.9141],
  'thrissur': [76.2144, 10.5276],
  'kozhikode': [75.7804, 11.2588],
};

const IndiaJobsMap = () => {
  const [mapData, setMapData] = useState([]);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchMapData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/listings/map`);
      // Support both new shape ({cities, states}) and legacy shape (array)
      const cities = Array.isArray(data) ? data : data?.cities || [];
      setMapData(cities);
    } catch (error) {
      console.error('Error fetching map data:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchMapData();
  }, [fetchMapData]);

  const getCoords = (cityName) => {
    const key = cityName.toLowerCase().trim();
    return CITY_COORDS[key] || null;
  };

  const markersWithCoords = mapData
    .map((item) => ({ ...item, coords: getCoords(item.city) }))
    .filter((item) => item.coords);

  if (markersWithCoords.length === 0) return null;

  const maxCount = Math.max(...markersWithCoords.map((m) => m.count), 1);

  return (
    <div data-testid="india-jobs-map">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ center: [82, 22], scale: 1000 }}
        width={900}
        height={920}
        style={{ width: '100%', height: 'auto' }}
      >
        <Geographies geography={INDIA_TOPO}>
          {({ geographies }) =>
            geographies
              .filter((geo) => geo.properties.name === 'India')
              .map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#0F2942"
                  stroke="#0F2942"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none', fill: '#0F2942' },
                    pressed: { outline: 'none' }
                  }}
                />
              ))
          }
        </Geographies>

        {markersWithCoords.map((marker) => {
          const size = 6 + (marker.count / maxCount) * 14;
          return (
            <Marker
              key={marker.city}
              coordinates={marker.coords}
              onClick={() => navigate(`/browse?city=${encodeURIComponent(marker.city)}`)}
              style={{ cursor: 'pointer' }}
            >
              <circle
                r={size}
                fill="#D4820A"
                fillOpacity={0.85}
                stroke="#FFFFFF"
                strokeWidth={1.5}
              />
              <text
                textAnchor="middle"
                y={size + 14}
                style={{
                  fontFamily: 'Work Sans, sans-serif',
                  fontSize: '11px',
                  fill: '#FFFFFF',
                  fontWeight: 500,
                  pointerEvents: 'none'
                }}
              >
                {marker.city}
              </text>
              <text
                textAnchor="middle"
                y={-size - 6}
                style={{
                  fontFamily: 'Work Sans, sans-serif',
                  fontSize: '11px',
                  fill: '#D4820A',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  pointerEvents: 'none'
                }}
              >
                +{marker.count}
              </text>
            </Marker>
          );
        })}
      </ComposableMap>
    </div>
  );
};

export default IndiaJobsMap;
